var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement } from 'lit';
import { property, state } from 'lit/decorators.js';
import { TimePropertyModeEnum } from 'ngeo/datasource/OGC';
/**
 * Base class for OGC time based input component (slider, datepicker)
 * The "time" property (config) is mandatory.
 * Emits a custom event "change" with the selected value.
 */
export default class GmfTimeInput extends LitElement {
    set time(timeObj) {
        if (this._time === timeObj) {
            return;
        }
        this._time = timeObj;
        if (timeObj) {
            this.getCorrectTimeObject();
            this.setupMinMaxDefaultValues();
            // Wait a bit that listener are ready.
            setTimeout(() => this.emitChangeEvent(), 100);
        }
    }
    get time() {
        return this._time;
    }
    /**
     * Set up the start and the end date based on the time attribute.
     * @protected
     */
    setupMinMaxDefaultValues() {
        this.dateStart = +new Date(this.timeProp.minDefValue ?? this.timeProp.minValue);
        this.dateEnd = +new Date(this.timeProp.maxDefValue ?? this.timeProp.maxValue);
        this.updateTime(this.dateStart, this.dateEnd);
    }
    /**
     * Update start time on input change.
     * @param event input event.
     * @protected
     */
    onDateStartSelected(event) {
        const target = event.target;
        this.updateTime(+new Date(target.value));
        this.emitChangeEvent();
    }
    /**
     * Update end time on input change.
     * @param event input event.
     * @protected
     */
    onDateEndSelected(event) {
        const target = event.target;
        this.updateTime(this.dateStart, +new Date(target.value));
        this.emitChangeEvent();
    }
    /**
     * Updates the displayed time.
     * @param dateStart start timestamp.
     * @param dateEnd optional end timestamp.
     * @protected
     */
    updateTime(dateStart, dateEnd) {
        this.dateStart = dateStart;
        if (dateEnd) {
            this.dateEnd = dateEnd;
        }
    }
    /**
     * Dispatch a "change" event with the new time value.
     * @protected
     */
    emitChangeEvent() {
        const time = {
            start: this.dateStart,
        };
        if (this.isTimeRange()) {
            time.end = this.dateEnd;
        }
        const changeEvent = new CustomEvent('change', {
            bubbles: true,
            composed: true,
            detail: time,
        });
        this.dispatchEvent(changeEvent);
    }
    /**
     * Get correct input time object form provided time object.
     * @protected
     */
    getCorrectTimeObject() {
        this.timeProp = { ...this.time };
        if (this.time.minValue) {
            this.timeProp.minValue = this.asIsoDateString(this.time.minValue);
        }
        if (this.time.maxValue) {
            this.timeProp.maxValue = this.asIsoDateString(this.time.maxValue);
        }
        if (this.timeProp.minDefValue) {
            this.timeProp.minDefValue = this.asIsoDateString(this.time.minDefValue);
        }
        if (this.time.maxDefValue) {
            this.timeProp.maxDefValue = this.asIsoDateString(this.time.maxDefValue);
        }
    }
    /**
     * @param dateTxt a string date like '2006-12-01T00:00:00Z'
     * @returns a string iso date like '2006-12-01'.
     * @protected
     */
    asIsoDateString(dateTxt) {
        return new Date(dateTxt).toISOString().slice(0, 10);
    }
    /**
     * @returns true if the "time range" mode must be displayed.
     * @protected
     */
    isTimeRange() {
        return this.timeProp.mode === TimePropertyModeEnum.RANGE;
    }
}
__decorate([
    property({ type: Object })
    /**
     * On time set, update the state properties.
     * Don't do that in lit "updated" to avoid loop.
     */
], GmfTimeInput.prototype, "time", null);
__decorate([
    state()
], GmfTimeInput.prototype, "timeProp", void 0);
__decorate([
    state()
], GmfTimeInput.prototype, "dateStart", void 0);
__decorate([
    state()
], GmfTimeInput.prototype, "dateEnd", void 0);
//# sourceMappingURL=time-input.js.map